import { useState } from "react";

type MutationStatus = "idle" | "loading" | "success" | "error";

export type Mutation<TData, TError> = {
    data: TData | undefined;
    error: TError | undefined;
    status: MutationStatus;
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    mutate: (variables: any) => Promise<TData | undefined>;
}

export function useMutation<TData, TError = unknown, TVariables = void>(
    mutationFn: (variables: TVariables) => Promise<TData>
): Mutation<TData, TError> {
    const [data, setData] = useState<TData | undefined>(undefined);
    const [error, setError] = useState<TError | undefined>(undefined);
    const [status, setStatus] = useState<MutationStatus>("idle");

    const mutate = async (variables: TVariables): Promise<TData | undefined> => {
        setStatus("loading");
        setError(undefined);
        setData(undefined);

        try {
            const result = await mutationFn(variables);
            setData(result);
            setStatus("success");
            return result;
        } catch (err) {
            setError(err as TError);
            setStatus("error");
            return undefined;
        }
    };

    return {
        data,
        error,
        status,
        isLoading: status === "loading",
        isError: status === "error",
        isSuccess: status === "success",
        mutate,
    };
}